import { ChangeDetectorRef, Component, Inject, Input, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidationErrors, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { QuickApplyApiService } from "../quick-apply-api.service";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { ActivatedRoute, Router } from "@angular/router";
import { EnvironmentToken } from "../../../../util";
import { Environment, TalentEmploymentAuthorizationRequest } from "../../../../typings";
import { urlValidator } from '../../shared/validators/urlValidator';
import { Country } from 'src/app/gather/enums';
import { Countries, FileUploadUserType, PersonUriType, TalentStatus } from 'src/app/enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QuickApplyWorkflowService } from "../quick-apply-workflow.service";
import { LocationPickerComponent } from '../../shared/components/location-picker/location-picker.component';
import { TalentProfileApiService } from '../../talent-profile/talent-profile-api.service';
import { TranslateService } from '@ngx-translate/core';
import { getJobBoardUrl } from '../../shared/util/talent-happy-path-util';

export const totalLinksValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const profileUrls = control.get('profileUrls') as FormArray;
  const portfolioUrls = control.get('portfolioUrls') as FormArray;

  // Count profiles that have text
  const profileCount = profileUrls?.controls.filter(c => !!c.value.url?.trim()).length || 0;

  // Count portfolios that have a URL entered
  const portfolioCount = portfolioUrls?.controls.filter(c => !!c.value.url?.trim()).length || 0;

  const total = profileCount + portfolioCount;

  return total > 6 ? { 'tooManyLinks': { actual: total, limit: 6 } } : null;
};

const ORDER_COUNTRY_CODES: { [countryId: number]: string } = {
  [Countries.USA]: 'US',
  [Countries.CA]: 'CA'
};

const acceptableAnswerValidator = (acceptable: boolean): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => control.value === acceptable ? null : { unacceptableAnswer: true };

@Component({
  selector: 'app-quick-apply-submit',
  standalone: false,
  templateUrl: './quick-apply-submit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './quick-apply-submit.component.scss'
})
export class QuickApplySubmitComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(LocationPickerComponent) locationPicker: LocationPickerComponent;

  @Input() resumeUploaded: boolean;
  @Input() resumeUploading: boolean;

  profileForm: FormGroup;
  email: string = '';
  talent: Talent;
  posting: PostingInfo;
  resumeDetail: ResumeDetail;
  profileUrlChanged: boolean = false;
  matUrl: string;
  tosUrl: string;
  hasSubmitError: boolean = false;
  submitErrorId: string;
  resumeReq: boolean = false;
  termsAccepted: boolean = false;
  disableSubmit: boolean = false;
  homeLocation: string;
  geoLocation: GeoLocation;
  locationValid: boolean = false;
  marketingData: MarketingTrackingInfo;
  displayCommunicationPreference: boolean = false;
  displayDoubleOptIn: boolean = false;
  displayCommunicationPreferenceCard: boolean = false;
  fileUploadUserType: FileUploadUserType = FileUploadUserType.QUICK_APPLY;
  rirekishoResume: File;
  keirekishoResume: File;
  Countries = Countries;
  countryId: number;
  isGeneralApplication: boolean = false;
  pageType: string;
  hidePassword = true;
  deletedUriIds: number[] = [];
  isLocationMismatch: boolean = false;
  isLocationMismatchModalVisible: boolean = false;
  orderCountryName: string;
  jobBoardUrl: string;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private quickApplyService: QuickApplyApiService,
    protected talentHappyPathDataService: TalentHappyPathDataService,
    private activatedRoute: ActivatedRoute,
    private workflowService: QuickApplyWorkflowService,
    private talentProfileApiService: TalentProfileApiService,
    private translate: TranslateService,
    @Inject(EnvironmentToken) private environment: Environment) {
    this.matUrl = this.environment.matHost;
    this.posting = this.talentHappyPathDataService.postingInfo;
  }

  ngOnInit(): void {
    this.isGeneralApplication = this.talentHappyPathDataService.isGeneralApplication
    this.route.data.subscribe(data => {
      if (data.talent) {
        this.talentHappyPathDataService.talent = data.talent;
      }
      this.talent = this.talentHappyPathDataService.talent;
      if (this.isGeneralApplication) {
        this.countryId = this.talent.countryId;
        this.talentHappyPathDataService.homeMarketCountryId = this.countryId;
        // A general application has no posting, so the posting-driven setTosUrl
        // calls never run — derive the ToS URL from the talent's country here
        // (e.g. Japan -> https://aquent.co.jp/terms/ rather than the matHost default).
        this.setTosUrl(this.countryId);
        this.redirectReturningCandidate();
      } else {
        // Make sure the posting is available before reading its country. If the
        // data service wasn't populated (e.g. a deep link or page reload that
        // reaches this page before the container loaded it), refetch by posting
        // id and repopulate it.
        this.posting = this.talentHappyPathDataService.postingInfo;
        if (this.posting) {
          this.applyPostingCountry(this.posting);
          this.redirectReturningCandidate();
        } else {
          // refetchPostingInfo resolves the country asynchronously and runs the
          // returning-candidate redirect itself once countryId is known.
          this.refetchPostingInfo();
        }
      }
    })

    // Initialize the form - note we're removing the required validator for homeLocation initially

    const phoneValidators = (this.isNewTHPTalent() || this.isGeneralApplication) ? [Validators.required, Validators.pattern(/.*\S.*/)] : [];
    this.profileForm = new FormGroup({
      firstName: new FormControl('', [Validators.required, Validators.pattern(/.*\S.*/)]), // at least one non-whitespace character
      lastName: new FormControl('', [Validators.required, Validators.pattern(/.*\S.*/)]),
      phone: new FormControl('', phoneValidators),
      homeLocation: new FormControl('', [
        Validators.required,
        this.placeValidator()
      ]),
      termsAccepted: new FormControl(false, [Validators.requiredTrue]),
      communicationPreference: new FormControl(''),
      doubleOptIn: new FormControl(false),
      profileUrls: new FormArray([]),
      portfolioUrls: new FormArray([]),
    }, { validators: totalLinksValidator });

    if (this.talent.homeLocation) {
      this.profileForm.patchValue({
        homeLocation: this.talent.homeLocation
      });
      this.homeLocation = this.talent.homeLocation; // Also set the component property
      if (this.talent.geoLocation?.latitude != 0 && this.talent.geoLocation?.longitude != 0) {
        this.geoLocation = this.talent.geoLocation;
      }
    }

    this.posting = this.talentHappyPathDataService.postingInfo;

    if (this.talent && !this.isNewTHPTalent()) {
      this.patchUris(this.talent.uris);
      if (this.talent.resumeData) {
        this.resumeDetail = this.talent.resumeData;
        this.talentHappyPathDataService.resumeDetail = this.talent.resumeData;
      }

      if (this.talent.firstName) {
        this.profileForm.patchValue({
          firstName: this.talent.firstName
        });
      }
      if(this.talent.lastName) {
        this.profileForm.patchValue({
          lastName: this.talent.lastName
        });
      }
      if (this.talent.phoneNumber) {
        this.profileForm.patchValue({
          phone: this.talent.phoneNumber
        });
      }
    }
    if (this.isNewTHPTalent()) {
      this.addProfileUrl();
      this.addPortfolioUrl();
    }

   this.setDisplayCommunicationPreference();
   this.setDisplayDoubleOptIn();

    if (this.posting?.countryId) {
      this.setTosUrl(this.posting.countryId);
    }

    this.marketingData = this.talentHappyPathDataService.marketingData;
    if (this.marketingData == null) {
      this.activatedRoute.queryParams.subscribe(params => {
        console.log("params ", params);
        if (Object.keys(params).length > 0) {
          let marketingData = <MarketingTrackingInfo>{};
          marketingData.url = params["url"];
          marketingData.utmCampaign = params["utm_campaign"];
          marketingData.utmMedium = params["utm_medium"];
          marketingData.utmSource = params["utm_source"];
          marketingData.utmTerm = params["utm_term"];
          marketingData.utmContent = params["utm_content"];
          marketingData.gclid = params["gclid"];
          this.talentHappyPathDataService.marketingData = marketingData;
        }
      });
    }

    this.resumeUploading = false;

    this.profileForm.get('homeLocation').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.profileForm.get('geoLocation')?.setValue(null, { emitEvent: false });
        this.profileForm.get('homeLocationCountryCode')?.setValue(null, { emitEvent: false });
        this.profileForm.get('countryId')?.setValue(null, { emitEvent: false });
      });
  }

  ngAfterViewInit(): void {
    if (!this.isGeneralApplication && this.homeLocation && this.locationPicker) {
      this.locationPicker.setLocationValue(this.homeLocation);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Re-fetches the posting when the data service has none cached, then
   * repopulates the data service and re-derives the posting-dependent state
   * (home-market country, ToS URL).
   */
  private refetchPostingInfo(): void {
    const postingId = this.resolvePostingId();
    if (!postingId) {
      return;
    }
    this.quickApplyService.readPostingInfo(String(postingId))
      .pipe(takeUntil(this.destroy$))
      .subscribe(posting => {
        if (!posting) {
          return;
        }
        this.posting = posting;
        this.talentHappyPathDataService.postingInfo = posting;
        this.talentHappyPathDataService.postingId = posting.jobPostingId;
        this.applyPostingCountry(posting);
        this.onHomeLocationCountryChange();
        this.setTosUrl(posting.countryId);
        // The synchronous ngOnInit path already ran these with countryId unset;
        // re-run now that the order country is known.
        this.setDisplayCommunicationPreference();
        this.setDisplayDoubleOptIn();
        this.redirectReturningCandidate();
      }, error => console.error(`Failed to load posting info: ${JSON.stringify(error)}`));
  }

  private applyPostingCountry(posting: PostingInfo): void {
    this.countryId = posting.countryId;
    this.talentHappyPathDataService.homeMarketCountryId = this.countryId;
    this.orderCountryName = this.translate.instant(`talent-happy-path.country-names.${this.countryId}`);
    this.jobBoardUrl = getJobBoardUrl(this.countryId);
  }

  onHomeLocationCountryChange(): void {
    const orderCountryCode = ORDER_COUNTRY_CODES[this.countryId];
    const homeCountryCode = this.profileForm.get('homeLocationCountryCode')?.value;
    if (!orderCountryCode || !homeCountryCode) {
      return;
    }
    const isMismatch = homeCountryCode.toUpperCase() !== orderCountryCode;
    if (isMismatch) {
      if (!this.isLocationMismatch) {
        this.profileForm.addControl('authorizedToWork', new FormControl(null, [acceptableAnswerValidator(true)]));
        this.profileForm.addControl('requiresSponsorship', new FormControl(null, [acceptableAnswerValidator(false)]));
      }
      this.isLocationMismatchModalVisible = true;
    } else if (this.isLocationMismatch) {
      this.profileForm.removeControl('authorizedToWork');
      this.profileForm.removeControl('requiresSponsorship');
    }
    this.isLocationMismatch = isMismatch;
  }

  hasUnacceptableAuthorizationAnswer(): boolean {
    if (!this.isLocationMismatch) {
      return false;
    }
    const authorizedToWork = this.profileForm.get('authorizedToWork')?.value;
    const requiresSponsorship = this.profileForm.get('requiresSponsorship')?.value;
    return authorizedToWork === false || requiresSponsorship === true;
  }

  private saveEmploymentAuthorization(talentId: number): void {
    const request: TalentEmploymentAuthorizationRequest = {
      talentId: talentId,
      authorizedToWork: this.profileForm.get('authorizedToWork').value,
      countryId: this.countryId,
      requiresSponsorship: this.profileForm.get('requiresSponsorship').value
    };
    this.talentHappyPathDataService.employmentAuthorization = request;
    this.talentProfileApiService.saveEmploymentAuthorization(request).subscribe(
      () => {},
      error => console.error('Error saving Authorization', error)
    );
  }

  /**
   * A candidate already on the order who previously applied via quick apply
   * skips the form: Japan goes straight to the thank-you page, everyone else to
   * the welcome state. Depends on countryId, so it runs once the order country
   * is known (which may be after an async posting refetch).
   */
  private redirectReturningCandidate(): void {
    const isCandidateOnOrder = this.talent.statusOnOrder > 0;
    if (this.talent.didApplyThroughQuickApply && isCandidateOnOrder) {
      if (this.countryId == Countries.JP) {
        this.router.navigateByUrl('thank-you', {skipLocationChange: true});
      } else {
        this.workflowService.navigateToState('welcome', {}, { relativeTo: this.route.parent });
      }
    }
  }

  /** Resolves the posting id from the data service, falling back to the route tree. */
  private resolvePostingId(): number | null {
    if (this.talentHappyPathDataService.postingId) {
      return this.talentHappyPathDataService.postingId;
    }
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const id = route.snapshot.paramMap.get('postingId');
      if (id) {
        const parsed = Number(id);
        return Number.isNaN(parsed) ? null : parsed;
      }
      route = route.parent;
    }
    return null;
  }

  private setTosUrl(countryId: number): void {
    switch (countryId) {
      case Country.France:
        this.tosUrl = this.matUrl + "/html/termsOfServiceFr.html";
        break;
      case Country.Japan:
        this.tosUrl = "https://aquent.co.jp/terms/";
        break;
      default:
        this.tosUrl = this.matUrl + "/html/termsOfService.html";
    }
  }

  isGeneralApplicationUpdatePage() {
    const url = window.location.href;
    const segments = url.split('/');
    this.pageType = segments[segments.length - 1];
    return this.pageType === 'update' || this.pageType === 'new';
  }

  private placeValidator() {
    return (control: FormControl): ValidationErrors | null => {
      const value = control.value;

      // An empty value is handled by Validators.required — don't also flag
      // invalidPlace, otherwise both error messages render at the same time.
      if (value == null || (typeof value === 'string' && value.trim() === '')) {
        return null;
      }

      // If there's no geoLocation and the value is just text (no selection made)
      const geoLocation = this.profileForm?.get('geoLocation')?.value
        || (value === this.talent?.homeLocation ? this.geoLocation : null);
      if ((!geoLocation || !geoLocation.latitude) && typeof value === 'string') {
        return { invalidPlace: true };
      }

      return null;
    };
  }

  submitCriteriaMet(): boolean {
    // used to control submit button disabled state
    if (this.talent?.personId && !this.isNewTHPTalent()) {
      this.resumeReq = true;
    } else {
      this.resumeReq = this.resumeUploaded;
    }

    return !this.profileForm.valid || !this.resumeReq;
  }

  submit(form: FormGroup): void {
    this.disableSubmit = true;
    this.resumeDetail = this.talentHappyPathDataService.resumeDetail;
    this.talent.firstName = form.value.firstName.trim();
    this.talent.lastName = form.value.lastName.trim();
    this.talent.phoneNumber = form.value.phone;
    this.talent.resumeData = this.resumeDetail;
    this.talent.resumeUpdated = this.resumeUploaded;
    this.talent.homeLocation = this.profileForm.get('homeLocation').value ? this.profileForm.get('homeLocation').value : this.talent.homeLocation;
    this.talent.geoLocation = this.profileForm.get('geoLocation').value? this.profileForm.get('geoLocation').value : this.talent.geoLocation;
    // The home location's own country decides whether the talent lands in "All Markets" (BIZ-40245). This is not
    // talent.countryId, which is the application/posting country.
    this.talent.homeLocationCountryCode = this.profileForm.get('homeLocationCountryCode')?.value
      ? this.profileForm.get('homeLocationCountryCode').value : this.talent.homeLocationCountryCode;
    this.talent.smsOptIn = this.displayCommunicationPreference ? form.value.communicationPreference : false;
    this.talent.doubleOptIn = this.displayDoubleOptIn ? form.value.doubleOptIn : false;
    this.marketingData = this.talentHappyPathDataService.marketingData;
    this.talent.deletedUriIds = this.deletedUriIds;

    let resumeFiles: File[] = [];
    if (this.isJapanOrder()) {
      // Send an empty File that signals the controller to fetch the earlier latest japan resume from zip file to re-parse with keirekishoResume
      if(!this.rirekishoResume) {
        this.rirekishoResume = new File([], '');
      }
      resumeFiles.push(this.rirekishoResume);
      if(this.keirekishoResume) {
        resumeFiles.push(this.keirekishoResume);
      }
    }
    const profiles: PersonUri[] = form.value.profileUrls
      .filter((item: any) => !!item.url?.trim())
      .map((item: any) => {
        return <PersonUri>({
          personUriId: item.personUriId,
          uri: item.url.trim(),
          type: PersonUriType.PROFILE,
          personId: this.talent.personId
        });
      });

    const portfolios: PersonUri[] = form.value.portfolioUrls
      .filter((item: any) => !!item.url?.trim())
      .map((item: any) => {
        return <PersonUri>({
          personUriId: item.personUriId,
          uri: item.url.trim(),
          password: item.password,
          type: PersonUriType.PORTFOLIO,
          personId: this.talent.personId
        });
      });

    this.talent.uris = [...profiles, ...portfolios];
    const quickApplyInfo = <QuickApplyInfo>({
      talent: this.talent,
      orderId: this.posting?.orderId ?? null,
      postingId: this.posting?.jobPostingId,
      marketId: this.posting?.marketId,
      marketingData: this.marketingData,
      countryId: this.countryId,
      candidateInviteLinkFrom: this.talentHappyPathDataService.candidateInviteLinkFrom,
      generalApplication: this.isGeneralApplication
    });


    this.quickApplyService.apply(quickApplyInfo, resumeFiles).subscribe(data => {
        this.talentHappyPathDataService.talent = data;
        this.talentHappyPathDataService.talent.countryId = this.countryId;
        if (this.isLocationMismatch) {
          this.saveEmploymentAuthorization(data.personId);
        }

      if (this.countryId === Country.Japan || this.countryId === Country.France) {
        this.router.navigateByUrl('thank-you', {skipLocationChange: true});
      } else if (this.isGeneralApplication) {
        this.router.navigateByUrl('/talent-profile', {skipLocationChange: true});
        return;
      } else {
        this.router.navigateByUrl('/initial-interview', {skipLocationChange: true});
      }
    }, error => {
      this.hasSubmitError = true;
      this.submitErrorId = error.data.errorId;
      this.disableSubmit = false;
      console.error(`Error quick applying: ${JSON.stringify(error)}`);
    });
  }

  resumeUploadedHandler(event: boolean): void {
    this.resumeUploaded = event;
  }

  resumeUploadingHandler(event: boolean): void {
    this.resumeUploading = event;
  }

  onRirekishoResumeUpload(file: File): void {
    this.rirekishoResume = file;
  }

  onKeirekishoResumeUpload(file: File): void {
    this.keirekishoResume = file;
  }

  onCountryIdChange(countryId: number): void {
    this.countryId = countryId;
    if (this.talent) {
      this.talent.countryId = countryId;
      this.talentHappyPathDataService.talent = this.talent;
    }
    this.setDisplayCommunicationPreference();
    this.setDisplayDoubleOptIn();
  }

  onHomeLocationBlur(): void {
    const control = this.profileForm.get('homeLocation');
    control?.markAsTouched();
    this.displayHomeLocation();
  }
  /**
   * Display the existing home location when focus is lost.
   * This provides a fallback to show the user's current location if they didn't select a new one.
   */
  displayHomeLocation() {
    if (!this.locationValid && this.talent?.homeLocation) {
      this.profileForm.patchValue({
        homeLocation: this.talent.homeLocation
      });
      this.talent.locationUpdated = false;
    }
  }

  isJapanOrder() {
    return this.countryId == Countries.JP;
  }

  isNewTHPTalent() {
    if(this.talent?.statusOnOrder == TalentStatus.SUGGESTED_CANDIDATE) {
      return (this.talent?.newTHPTalent && this.talent?.personId > 0);
    }
    else {
      return !this.talent?.personId;
    }
  }

  getSubmitButtonText() {
    if (this.isGeneralApplication) {
      if (this.isNewTHPTalent()) {
        return 'talent-happy-path.quick-apply.submit-page.join-talent-community';
      } else {
        return 'talent-happy-path.quick-apply.submit-page.update-profile';
      }
    } else {
      return 'talent-happy-path.quick-apply.submit-page.submit-application'
    }
  }

  setDisplayCommunicationPreference() {
    const isSmsEligibleCountry = this.countryId === Country.US || this.countryId === Country.Canada || this.countryId === Country.Germany;

    if (this.countryId === Country.Germany) {
      this.displayCommunicationPreference = this.isNewTHPTalent() && isSmsEligibleCountry;
    }
    else {
      const isExistingUserWithoutConsent = !this.isNewTHPTalent() && !this.talent.smsOptIn;
      this.displayCommunicationPreference = (this.isNewTHPTalent() || isExistingUserWithoutConsent) && isSmsEligibleCountry;
    }

    this.displayCommunicationPreferenceCard = this.displayCommunicationPreference || this.displayDoubleOptIn;
  }

  setDisplayDoubleOptIn() {
    if (this.isNewTHPTalent()) {
      this.displayDoubleOptIn = this.countryId === Country.Germany;
    }
    else {
      const isExistingUserWithoutDoubleOptIn = !this.talent.doubleOptIn;
      this.displayDoubleOptIn = this.countryId === Country.Germany && isExistingUserWithoutDoubleOptIn;
    }

    this.displayCommunicationPreferenceCard = this.displayCommunicationPreference || this.displayDoubleOptIn;
  }

  get profileUrls() { return this.profileForm.get('profileUrls') as FormArray; }
  get portfolioUrls() { return this.profileForm.get('portfolioUrls') as FormArray; }

  addProfileUrl(value: string = '') {
    if (this.canAddMore) {
      this.profileUrls.push(new FormGroup({
        personUriId: new FormControl(),
        url: new FormControl('', [urlValidator()])
      }));
    }
  }
  addPortfolioUrl(data: any = { url: '', password: '', showPass: false }) {
    if (this.canAddMore) {
      this.portfolioUrls.push(new FormGroup({
        url: new FormControl(data.url, [urlValidator()]),
        password: new FormControl(data.password, [Validators.maxLength(100)]),
        showPassword: new FormControl(data.showPass),
        hidePassword: new FormControl(true)//UI only
      }));
    }
  }

  get canAddMore(): boolean {
    const profileArray = this.profileForm.get('profileUrls') as FormArray;
    const portfolioArray = this.profileForm.get('portfolioUrls') as FormArray;

    const activeProfiles = profileArray.controls.filter(c => !!c.value && c.value.url.trim() !== '').length;
    const activePortfolios = portfolioArray.controls.filter(group => !!group.value.url && group.value.url.trim() !== '').length;

    const totalActive = activeProfiles + activePortfolios;

    return totalActive < 6;
  }

  patchUris(existingUris: PersonUri[]) {
    this.profileUrls.clear();
    this.portfolioUrls.clear();
    if (!existingUris || existingUris.length === 0) {
      this.addProfileUrl();
      this.addPortfolioUrl();
      return;
    }
    existingUris.forEach(uri => {
      if (uri.type === PersonUriType.PROFILE) {
        this.profileUrls.push(new FormGroup({
          personUriId: new FormControl(uri.personUriId),
          url: new FormControl(uri.uri, [urlValidator()])
        }));
      } else if (uri.type === PersonUriType.PORTFOLIO) {
        this.portfolioUrls.push(new FormGroup({
          personUriId: new FormControl(uri.personUriId),
          url: new FormControl(uri.uri, [urlValidator()]),
          showPassword: new FormControl(!!uri.password), // Toggle on if password exists
          password: new FormControl(uri.password, [Validators.maxLength(100)]),
          hidePassword: new FormControl(true) //UI only
        }));
      }
    });
  }
  removeProfileUrl(index: number) {
    const control = this.profileUrls.at(index);
    const uriId = control.value?.personUriId;

    if (uriId) {
      this.deletedUriIds.push(uriId);
    }

    this.profileUrls.removeAt(index);
    this.profileForm.updateValueAndValidity();
  }

  removePortfolioUrl(index: number) {
    const group = this.portfolioUrls.at(index) as FormGroup;
    const uriId = group.get('personUriId')?.value;

    if (uriId) {
      this.deletedUriIds.push(uriId);
    }

    this.portfolioUrls.removeAt(index);
    this.profileForm.updateValueAndValidity();
  }
}
